import React, { useState, useEffect, useRef } from 'react';
import { 
  ShoppingCart, Trash2, Plus, Minus, ArrowLeft, CreditCard, 
  Loader2, Package, Building2, User, Tag, X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';
import { PaymentSuccess, PaymentFailed, PaymentFrame } from './checkout/PaymentViews';
import PriceEstimate from './PriceEstimate';

const Checkout = ({ onBackToShop, onBackToHome }) => {
  const { t, i18n } = useTranslation();
  const { cartItems, removeFromCart, updateQuantity, clearCart, getCartTotal } = useCart();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [showPaymentFrame, setShowPaymentFrame] = useState(false);
  const [paymentToken, setPaymentToken] = useState(null);
  const iframeRef = useRef(null);

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
    invoiceType: 'personal',
    companyName: '',
    vatNumber: '',
    companyAddress: ''
  });

  // Discount code state
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState(null); // { code, discount_amount, type, value, ... }
  const [promoError, setPromoError] = useState('');
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(price);
  };

  const subtotal = getCartTotal();
  const discountAmount = promo ? Number(promo.discount_amount || 0) : 0;
  // VAT applies only to individual / B2C services (Turkish KDV)
  const VAT_RATE = 0.20;
  const vatableBase = Math.max(
    cartItems
      .filter((it) => !it.service_type || it.service_type === 'individual')
      .reduce((sum, it) => sum + Number(it.price) * Number(it.quantity || 1), 0)
      - discountAmount,
    0,
  );
  const vatAmount = Math.round(vatableBase * VAT_RATE * 100) / 100;
  const grandTotal = Math.max(subtotal - discountAmount + vatAmount, 0);

  // Re-validate the applied promo when the cart changes (e.g. user removes an item).
  useEffect(() => {
    if (!promo) return;
    let cancelled = false;
    const revalidate = async () => {
      try {
        const r = await fetch(apiUrl('discount-codes/validate'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: promo.code,
            cart: cartItems.map((it) => ({
              id: it.id,
              service_type: it.service_type,
              price: it.price,
              quantity: it.quantity,
            })),
            email: customerInfo.email || undefined,
          }),
        });
        if (cancelled) return;
        if (!r.ok) {
          setPromo(null);
          setPromoError(i18n.language === 'tr' ? 'İndirim kodu artık geçerli değil.' : 'Discount no longer valid for this cart.');
        } else {
          const data = await r.json();
          setPromo(data);
          setPromoError('');
        }
      } catch (_) {
        // Network errors are non-fatal — keep the existing promo state.
      }
    };
    revalidate();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const handleApplyPromo = async () => {
    if (!promoInput.trim()) return;
    setIsApplyingPromo(true);
    setPromoError('');
    try {
      const r = await fetch(apiUrl('discount-codes/validate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: promoInput.trim(),
          cart: cartItems.map((it) => ({
            id: it.id,
            service_type: it.service_type,
            price: it.price,
            quantity: it.quantity,
          })),
          email: customerInfo.email || undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        const msg = mapPromoError(data?.detail || '');
        setPromoError(msg);
        setPromo(null);
        return;
      }
      setPromo(data);
      setPromoInput('');
      toast.success(
        i18n.language === 'tr'
          ? `Kupon uygulandı: -${formatPrice(data.discount_amount)}`
          : `Coupon applied: -${formatPrice(data.discount_amount)}`,
      );
    } catch (e) {
      logger.error('Promo validation failed:', e);
      setPromoError(i18n.language === 'tr' ? 'Kod doğrulanamadı, tekrar deneyin.' : 'Could not verify code, try again.');
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const mapPromoError = (detail) => {
    const tr = i18n.language === 'tr';
    const messages = {
      INVALID_CODE: tr ? 'Geçersiz indirim kodu.' : 'Invalid discount code.',
      INACTIVE_CODE: tr ? 'Bu kod artık etkin değil.' : 'This code is no longer active.',
      CODE_NOT_YET_ACTIVE: tr ? 'Bu kod henüz kullanıma açılmadı.' : 'This code is not active yet.',
      CODE_EXPIRED: tr ? 'Bu kodun süresi doldu.' : 'This code has expired.',
      CODE_USAGE_LIMIT_REACHED: tr ? 'Kod kullanım limiti dolmuş.' : 'This code reached its usage limit.',
      CODE_ALREADY_USED: tr ? 'Bu kodu daha önce kullandınız.' : 'You have already used this code.',
      CODE_NOT_APPLICABLE_TO_CART: tr ? 'Bu kod sepetinizdeki ürünlere uygulanamaz (sadece bireysel hizmetler).' : 'This code does not apply to your cart (individual services only).',
      MIN_SUBTOTAL_NOT_MET: tr ? 'Minimum sepet tutarına ulaşılmadı.' : 'Minimum cart total not reached.',
      EMPTY_CODE: tr ? 'Lütfen bir kod girin.' : 'Please enter a code.',
    };
    return messages[detail] || (tr ? 'Kod uygulanamadı.' : 'Could not apply code.');
  };

  const removePromo = () => {
    setPromo(null);
    setPromoError('');
  };

  // Check URL params for payment result (redirect from PayTR)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderIdFromUrl = urlParams.get('order_id');
    const paymentStatus = window.location.pathname;
    
    if (orderIdFromUrl) {
      if (paymentStatus.includes('payment-success')) {
        setOrderId(orderIdFromUrl);
        setOrderComplete(true);
        clearCart();
      } else if (paymentStatus.includes('payment-failure')) {
        setOrderId(orderIdFromUrl);
        setPaymentFailed(true);
      }
    }
  }, [clearCart]);

  // Load PayTR iFrame resizer script
  useEffect(() => {
    if (showPaymentFrame && paymentToken) {
      const script = document.createElement('script');
      script.src = 'https://www.paytr.com/js/iframeResizer.min.js';
      script.async = true;
      script.onload = () => {
        if (window.iFrameResize && iframeRef.current) {
          window.iFrameResize({}, '#paytriframe');
        }
      };
      document.body.appendChild(script);
      
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [showPaymentFrame, paymentToken]);

  const handleCreateOrder = async () => {
    if (!customerInfo.name || !customerInfo.email) {
      toast.error(t('checkout.fillRequired', 'Please fill in required fields'));
      return;
    }

    if (customerInfo.invoiceType === 'company') {
      if (!customerInfo.companyName || !customerInfo.vatNumber || !customerInfo.companyAddress) {
        toast.error(t('checkout.fillCompanyFields', 'Please fill in all company invoice fields'));
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // First create the order
      const orderResponse = await fetch(apiUrl("orders"), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cartItems.map(item => ({
            serviceId: item.id,
            quantity: item.quantity
          })),
          customerName: customerInfo.name,
          customerEmail: customerInfo.email,
          customerPhone: customerInfo.phone,
          customerAddress: customerInfo.address,
          customerCompany: customerInfo.invoiceType === 'company' ? customerInfo.companyName : null,
          invoiceType: customerInfo.invoiceType,
          vatNumber: customerInfo.invoiceType === 'company' ? customerInfo.vatNumber : null,
          companyAddress: customerInfo.invoiceType === 'company' ? customerInfo.companyAddress : null,
          notes: customerInfo.notes,
          discountCode: promo?.code || null
        })
      });

      const orderData = await orderResponse.json();

      if (!orderResponse.ok || !orderData.success) {
        toast.error(orderData.detail || t('checkout.orderFailed', 'Failed to create order'));
        setIsSubmitting(false);
        return;
      }

      const newOrderId = orderData.orderId;
      setOrderId(newOrderId);

      // Now initialize PayTR payment
      const paymentResponse = await fetch(apiUrl("payments/initialize"), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: newOrderId,
          customer_email: customerInfo.email,
          customer_name: customerInfo.name,
          customer_phone: customerInfo.phone || '5000000000',
          customer_address: customerInfo.address || 'Turkey',
          amount: orderData.totalAmount ?? grandTotal,
          currency: 'TL',
          items: cartItems.map(item => ({
            name: item.title,
            price: item.price,
            quantity: item.quantity
          }))
        })
      });

      const paymentData = await paymentResponse.json();

      if (paymentResponse.ok && paymentData.status === 'success') {
        setPaymentToken(paymentData.token);
        setShowPaymentFrame(true);
        toast.success(t('checkout.paymentReady', 'Payment form loaded. Please complete your payment.'));
      } else {
        toast.error(paymentData.detail || t('checkout.paymentFailed', 'Failed to initialize payment'));
      }
    } catch (error) {
      logger.error('Error creating order:', error);
      toast.error(t('checkout.orderFailed', 'Failed to create order'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Payment Success View
  if (orderComplete) {
    return <PaymentSuccess orderId={orderId} onBackToHome={onBackToHome} />;
  }

  // Payment Failed View
  if (paymentFailed) {
    return (
      <PaymentFailed
        orderId={orderId}
        onRetry={() => { setPaymentFailed(false); setShowPaymentFrame(false); }}
        onBackToHome={onBackToHome}
      />
    );
  }

  // Payment iFrame View
  if (showPaymentFrame && paymentToken) {
    return <PaymentFrame paymentToken={paymentToken} iframeRef={iframeRef} onBackToShop={() => { setShowPaymentFrame(false); setPaymentToken(null); }} />;
  }

  // Empty Cart View
  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center">
          <ShoppingCart className="w-20 h-20 text-gray-300 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('checkout.emptyCart', 'Your cart is empty')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('checkout.emptyCartMessage', 'Add some services to get started')}
          </p>
          <Button onClick={onBackToShop} className="bg-blue-600 hover:bg-blue-700">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('checkout.browseServices', 'Browse Services')}
          </Button>
        </div>
      </div>
    );
  }

  // Main Checkout View
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBackToShop}
              className="text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('checkout.continueShopping', 'Continue Shopping')}
            </button>
            <h1 className="text-2xl font-bold text-gray-900">{t('checkout.title', 'Checkout')}</h1>
            <div className="w-32"></div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items & Customer Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Cart Items */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                {t('checkout.cartItems', 'Cart Items')} ({cartItems.length})
              </h2>
              
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    {/* Image */}
                    <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0 overflow-hidden">
                      {item.image ? (
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{item.title}</h3>
                      <p className="text-sm text-gray-500">{item.category}</p>
                      <p className="text-blue-600 font-bold">{formatPrice(item.price)}</p>
                    </div>

                    {/* Quantity */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Subtotal */}
                    <div className="text-right">
                      <p className="font-bold text-gray-900">{formatPrice(item.price * item.quantity)}</p>
                      <PriceEstimate amount={item.price * item.quantity} className="block" />
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1 mt-1"
                      >
                        <Trash2 className="w-3 h-3" />
                        {t('checkout.remove', 'Remove')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer Information */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {t('checkout.customerInfo', 'Your Information')}
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('checkout.name', 'Full Name')} *
                  </label>
                  <Input
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ahmet Yılmaz"
                    required
                    data-testid="checkout-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('checkout.email', 'Email')} *
                  </label>
                  <Input
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="ahmet@sirket.com"
                    required
                    data-testid="checkout-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('checkout.phone', 'Phone')}
                  </label>
                  <Input
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="5XX XXX XX XX"
                    data-testid="checkout-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('checkout.address', 'Address')}
                  </label>
                  <Input
                    value={customerInfo.address}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Istanbul, Turkey"
                    data-testid="checkout-address"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('checkout.notes', 'Notes')}
                  </label>
                  <Textarea
                    value={customerInfo.notes}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder={t('checkout.notesPlaceholder', 'Any special requirements or questions?')}
                    rows={3}
                    data-testid="checkout-notes"
                  />
                </div>
              </div>
            </div>

            {/* Invoice Information */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {t('checkout.invoiceInfo', 'Invoice Information')}
              </h2>
              
              {/* Invoice Type Toggle */}
              <div className="flex gap-3 mb-6" data-testid="invoice-type-toggle">
                <button
                  type="button"
                  onClick={() => setCustomerInfo(prev => ({ ...prev, invoiceType: 'personal' }))}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                    customerInfo.invoiceType === 'personal'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                  data-testid="invoice-personal-btn"
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium">{t('checkout.personalInvoice', 'Personal')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCustomerInfo(prev => ({ ...prev, invoiceType: 'company' }))}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                    customerInfo.invoiceType === 'company'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                  data-testid="invoice-company-btn"
                >
                  <Building2 className="w-5 h-5" />
                  <span className="font-medium">{t('checkout.companyInvoice', 'Company')}</span>
                </button>
              </div>

              {/* Company Fields */}
              {customerInfo.invoiceType === 'company' && (
                <div className="grid md:grid-cols-2 gap-4 animate-in slide-in-from-top-2 duration-200" data-testid="company-fields">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('checkout.companyName', 'Company Name')} *
                    </label>
                    <Input
                      value={customerInfo.companyName}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, companyName: e.target.value }))}
                      placeholder={t('checkout.companyNamePlaceholder', 'ABC Ltd.')}
                      required
                      data-testid="checkout-company-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('checkout.vatNumber', 'VAT Number (Vergi No)')} *
                    </label>
                    <Input
                      value={customerInfo.vatNumber}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, vatNumber: e.target.value }))}
                      placeholder={t('checkout.vatNumberPlaceholder', 'e.g., 1234567890')}
                      required
                      data-testid="checkout-vat-number"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('checkout.companyAddress', 'Company Address')} *
                    </label>
                    <Input
                      value={customerInfo.companyAddress}
                      onChange={(e) => setCustomerInfo(prev => ({ ...prev, companyAddress: e.target.value }))}
                      placeholder={t('checkout.companyAddressPlaceholder', 'Full company billing address')}
                      required
                      data-testid="checkout-company-address"
                    />
                  </div>
                </div>
              )}

              {customerInfo.invoiceType === 'personal' && (
                <p className="text-sm text-gray-500 italic">
                  {t('checkout.personalInvoiceNote', 'Invoice will be issued to your personal name and address provided above.')}
                </p>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {t('checkout.orderSummary', 'Order Summary')}
              </h2>

              <div className="space-y-3 mb-6">
                {cartItems.map(item => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.title} × {item.quantity}
                    </span>
                    <span className="font-medium">{formatPrice(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-4 mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>{t('checkout.subtotal', 'Subtotal')}</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                {promo && discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-700 font-medium mb-1" data-testid="discount-line">
                    <span className="inline-flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      {promo.code}
                    </span>
                    <span>-{formatPrice(discountAmount)}</span>
                  </div>
                )}
                {vatAmount > 0 && (
                  <div className="flex justify-between text-sm text-gray-600 mb-1" data-testid="vat-line">
                    <span>{i18n.language === 'tr' ? 'KDV (%20)' : 'VAT (20%)'}</span>
                    <span>{formatPrice(vatAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold mt-2 pt-2 border-t border-gray-100">
                  <span>{t('checkout.total', 'Total')}</span>
                  <span className="text-blue-600">{formatPrice(grandTotal)}</span>
                </div>
                <div className="flex justify-end mt-1">
                  <PriceEstimate amount={grandTotal} />
                </div>
              </div>

              {/* Discount code input */}
              <div className="mb-6">
                {promo ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2" data-testid="applied-promo">
                    <div className="flex items-center gap-2 text-sm text-green-800">
                      <Tag className="w-4 h-4" />
                      <span className="font-semibold">{promo.code}</span>
                      <span className="text-green-600">
                        ({promo.type === 'percentage' ? `${promo.value}% off` : `-${formatPrice(promo.value)}`})
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={removePromo}
                      data-testid="remove-promo"
                      className="text-green-700 hover:text-green-900"
                      aria-label="Remove promo code"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      {t('checkout.promoCode', 'Promo code')}
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyPromo(); } }}
                        placeholder={i18n.language === 'tr' ? 'Kupon kodu' : 'Enter code'}
                        data-testid="promo-input"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        onClick={handleApplyPromo}
                        disabled={isApplyingPromo || !promoInput.trim()}
                        data-testid="apply-promo"
                        variant="outline"
                      >
                        {isApplyingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : t('checkout.apply', 'Apply')}
                      </Button>
                    </div>
                    {promoError && (
                      <p className="text-xs text-red-600 mt-1.5" data-testid="promo-error">{promoError}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Invoice Details in Summary */}
              {customerInfo.invoiceType === 'company' && customerInfo.companyName && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm" data-testid="summary-invoice-details">
                  <p className="font-medium text-gray-700 mb-1">{t('checkout.invoiceDetails', 'Invoice Details')}</p>
                  <p className="text-gray-600">{customerInfo.companyName}</p>
                  {customerInfo.vatNumber && (
                    <p className="text-gray-500">{t('checkout.vatLabel', 'VAT')}: {customerInfo.vatNumber}</p>
                  )}
                  {customerInfo.companyAddress && (
                    <p className="text-gray-500">{customerInfo.companyAddress}</p>
                  )}
                </div>
              )}

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-green-800">
                  <CreditCard className="w-5 h-5" />
                  <span className="font-medium">{t('checkout.securePayment', 'Secure Payment')}</span>
                </div>
                <p className="text-sm text-green-700 mt-1">
                  {t('checkout.paytrInfo', 'Pay securely with credit/debit card via PayTR')}
                </p>
                <div className="mt-3 flex items-center justify-start">
                  <img
                    src="/paytr-badge.png"
                    alt="PayTR Güvenli Öde – Visa, Mastercard, Troy"
                    className="h-12 w-auto"
                    data-testid="paytr-logo-checkout"
                  />
                </div>
              </div>

              <Button
                onClick={handleCreateOrder}
                disabled={isSubmitting || !customerInfo.name || !customerInfo.email || 
                  (customerInfo.invoiceType === 'company' && (!customerInfo.companyName || !customerInfo.vatNumber || !customerInfo.companyAddress))}
                className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg"
                data-testid="proceed-to-payment-btn"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-5 h-5 mr-2" />
                )}
                {t('checkout.proceedToPayment', 'Proceed to Payment')}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                {t('checkout.secureNote', 'Your payment information is encrypted and secure.')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
